import css from "./PinnedMessage.module.css"
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { Chat } from "@/reducers/chatsSlice"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  latestMissingPinnedMessage,
  pinnedMessagesFromChat,
} from "@/reducers/pinnedSliceSelectors"
import { ChatHistoryContext } from "@/contexts/ChatHistoryContext"
import TextTruncate from "../TextTruncate"
import { PinnedMessage, removePinnedMessage } from "@/api/xeps/PinnedMessages"
import { XmppContext } from "@/stream/xmppClient"
import { fetchMessagesFromPast } from "@/listeners/roomSync"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { ChevronsUpDown, PinOff } from "lucide-react"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"
import type { AccountChat } from "@/lib/accountChatsParser"
import { getTransicsMessagePresentation } from "@/lib/transicsMessagePresentation"

type PinnedMessageProps = {
  chat: Chat
}

const MessageOverview = ({
  message,
  accountChat,
}: {
  message: PinnedMessage["message"]
  accountChat?: AccountChat
}) => {
  const { t } = useTranslation()
  let overviewText: string

  if (message.url) {
    if (message.url.match(/\.(pdf)$/i)) {
      overviewText = t("chat.pinned.pdfFile")
    } else if (message.url.match(/\.(jpg|jpeg|png)$/i)) {
      overviewText = t("chat.pinned.image")
    } else if (message.url.match(/\.(oga)$/i)) {
      overviewText = t("chat.pinned.voiceMessage")
    } else {
      overviewText = t("chat.pinned.file")
    }
  } else {
    overviewText =
      getTransicsMessagePresentation(message, accountChat)?.body ?? message.body
  }

  return <TextTruncate text={overviewText} />
}

const PinnedMessage = ({ chat }: PinnedMessageProps) => {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)

  const { jumpAndHighlightMessage } = useContext(ChatHistoryContext)
  const pinnedMessagesSelector = useMemo(
    () => pinnedMessagesFromChat(chat.jid),
    [chat.jid],
  )
  const pinnedMessages = useAppSelector(pinnedMessagesSelector)
  const accountChatSelector = useMemo(
    () => selectAccountChatByJid(chat.jid),
    [chat.jid],
  )
  const accountChat = useAppSelector(accountChatSelector)

  const dispatch = useAppDispatch()

  const latestMissingPinnedMessageSelector = useMemo(
    () => latestMissingPinnedMessage(chat.jid),
    [chat.jid],
  )
  const [hasFetched, setHasFetched] = useState<boolean>(false)
  const missingPinedMessage = useAppSelector(latestMissingPinnedMessageSelector)
  const [activePinnedMessage, setActivePinnedMessage] = useState<number>(0)

  useEffect(() => {
    if (
      activePinnedMessage !== 0 &&
      activePinnedMessage >= pinnedMessages.length
    ) {
      setActivePinnedMessage(pinnedMessages.length - 1)
    }
  }, [pinnedMessages, activePinnedMessage])

  useEffect(() => {
    // Set to latest message when new message is pinned
    if (pinnedMessages.length > 0) {
      setActivePinnedMessage(pinnedMessages.length - 1)
    }
  }, [pinnedMessages.length])

  useEffect(() => {
    const fetchMessages = async () => {
      if (missingPinedMessage !== undefined && !hasFetched) {
        await fetchMessagesFromPast(
          chat,
          missingPinedMessage,
          client,
          myJid.toString(),
          dispatch,
        )
        setHasFetched(true)
      }
    }
    fetchMessages()
  }, [chat, client, hasFetched, dispatch, myJid, missingPinedMessage])

  const pinnedMessage = pinnedMessages[activePinnedMessage]

  const handleMessageClick = useCallback(() => {
    jumpAndHighlightMessage(pinnedMessage?.message.id)
  }, [pinnedMessage, jumpAndHighlightMessage])

  const handleNavigateNext = useCallback(() => {
    setActivePinnedMessage((prev) => {
      const newIndex = (prev + 1) % pinnedMessages.length
      jumpAndHighlightMessage(pinnedMessages[newIndex]?.message.id)
      return newIndex
    })
  }, [jumpAndHighlightMessage, pinnedMessages])

  const handleUnpinMessage = useCallback(async () => {
    await removePinnedMessage(client, pinnedMessage.itemId)
  }, [client, pinnedMessage])

  if (pinnedMessages.length === 0) return null
  return (
    <div
      className="max-w-screen border-base relative flex min-h-8 shrink-0 cursor-pointer flex-row flex-nowrap items-center gap-2.5 overflow-hidden border-b bg-muted px-5 py-1.5 text-sm text-foreground"
      data-testid="pinned-messages"
    >
      <div className={css.messageContent} onClick={handleMessageClick}>
        {pinnedMessage?.message && (
          <MessageOverview
            message={pinnedMessage?.message}
            accountChat={accountChat}
          />
        )}
      </div>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              data-testid="unpin-message"
              variant="ghost"
              size="icon"
              onClick={handleUnpinMessage}
            >
              <PinOff className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          {pinnedMessages.length > 1 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="navigate-pinned-message"
                    variant="ghost"
                    size="icon"
                    onClick={handleNavigateNext}
                  >
                    <ChevronsUpDown className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p>{t("chat.pinned.next")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <TooltipContent side="left">
            <p>{t("chat.message.actions.unpin")}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

export default PinnedMessage
